import type { LoanOrderFacts } from "@scs/contracts";
import { useCallback, useEffect, useState } from "react";
import type { FormEvent, KeyboardEvent } from "react";
import { ApiError } from "../api/client.js";
import type { Customer, CustomerAssignment, CustomerPatch, LoanFactCandidate, LoanOrder, PublicUser } from "../api/client.js";

export function CustomerOrderPanel(props: {
  canEdit: boolean;
  canManageRestrictedFields: boolean;
  canReassign: boolean;
  collectors: readonly PublicUser[];
  customer: Customer;
  candidates?: readonly LoanFactCandidate[];
  getHistory: (customerId: string) => Promise<CustomerAssignment[]>;
  onCreate: (facts: LoanOrderFacts) => Promise<void>;
  onReviewCandidate?: (id: string, action: "CONFIRM" | "REJECT", reason?: string) => Promise<void>;
  onReassign: (customerId: string, collectorId: string) => Promise<void>;
  onUpdateCustomer: (id: string, patch: CustomerPatch) => Promise<void>;
  onUpdate: (id: string, facts: LoanOrderFacts) => Promise<void>;
  orders: readonly LoanOrder[];
  showCustomer?: boolean;
  showOrders?: boolean;
}): React.JSX.Element {
  const [draft, setDraft] = useState({
    assignedCollectorId: props.customer.assignedCollectorId,
    borrowedAmount: "",
    borrowedCurrency: "",
    currency: "",
    currentOverdueAmount: "",
    customerLanguage: props.customer.language,
    daysPastDue: "",
    dueDate: "",
    loanNumber: "",
    loanAppName: "",
    paymentLink: "",
    repaymentMethod: "",
    totalDueAmount: ""
  });
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [candidateOrderPrefill, setCandidateOrderPrefill] = useState<{ candidate: LoanFactCandidate; orderId: string } | null>(null);
  const clearCandidateOrderPrefill = useCallback(() => setCandidateOrderPrefill(null), []);

  async function create(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setInvalidFields([]);
    try {
      await props.onCreate({
        assignedCollectorId: draft.assignedCollectorId,
        borrowedAmount: draft.borrowedAmount || null,
        borrowedCurrency: draft.borrowedCurrency.toUpperCase() || null,
        currency: draft.currency.toUpperCase(),
        currentOverdueAmount: draft.currentOverdueAmount,
        customerLanguage: draft.customerLanguage,
        daysPastDue: Number(draft.daysPastDue),
        dueDate: draft.dueDate,
        loanNumber: draft.loanNumber,
        loanAppName: draft.loanAppName || null,
        paymentLink: draft.paymentLink,
        repaymentMethod: draft.repaymentMethod || null,
        totalDueAmount: draft.totalDueAmount
      });
      setDraft((current) => ({
        ...current,
        currency: "",
        borrowedAmount: "",
        borrowedCurrency: "",
        currentOverdueAmount: "",
        daysPastDue: "",
        dueDate: "",
        loanNumber: "",
        loanAppName: "",
        paymentLink: "",
        repaymentMethod: "",
        totalDueAmount: ""
      }));
    } catch (caught) {
      const fields = invalidOrderFields(caught);
      setInvalidFields(fields);
      setError(fields.length === 0
        ? "创建订单失败。"
        : `请修正字段：${fields.join("、")}`);
    }
  }

  function update(field: keyof typeof draft, value: string): void {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  return (
    <section aria-label="客户与订单" className={props.showCustomer === false ? "inspector-orders" : undefined}>
      <h2>{props.showOrders === false ? "客户资料" : props.showCustomer === false ? "贷款订单" : "客户与订单"}</h2>
      {props.showCustomer === false ? null : <CustomerProfile
        canEdit={props.canEdit}
        canManageRestrictedFields={props.canManageRestrictedFields}
        customer={props.customer}
        key={props.customer.id}
        onUpdate={props.onUpdateCustomer}
      />}
      {props.showCustomer === false || !props.canReassign ? null : <AssignmentControl
        collectors={props.collectors}
        customer={props.customer}
        getHistory={props.getHistory}
        onReassign={props.onReassign}
      />}
      {props.showOrders === false ? null : <>
      {props.showCustomer === false ? null : <h3>贷款订单</h3>}
      <LoanCandidateReview
        canEdit={props.canEdit}
        candidates={props.candidates ?? []}
        orders={props.orders}
        {...(props.onReviewCandidate === undefined ? {} : { onReview: props.onReviewCandidate })}
        onPrefill={(candidate, orderId) => {
          if (orderId !== null) {
            setCandidateOrderPrefill({ candidate, orderId });
            return;
          }
          setDraft((current) => ({
            ...current,
            currency: candidate.facts.repaymentCurrency.value ?? candidate.facts.borrowedCurrency.value ?? current.currency,
            borrowedAmount: candidate.facts.borrowedAmount.value ?? current.borrowedAmount,
            borrowedCurrency: candidate.facts.borrowedCurrency.value ?? current.borrowedCurrency,
            dueDate: candidate.facts.dueDate.value ?? current.dueDate,
            loanAppName: candidate.facts.appName.value ?? current.loanAppName,
            paymentLink: candidate.facts.repaymentLink.value ?? current.paymentLink,
            repaymentMethod: candidate.facts.repaymentMethod.value ?? current.repaymentMethod,
            totalDueAmount: candidate.facts.repaymentAmount.value ?? current.totalDueAmount
          }));
        }}
      />
      {props.canEdit && props.orders.length === 0 ? (
        <form aria-label="新增订单" className="inspector-order-form" onSubmit={(event) => { void create(event); }}>
          <label>贷款编号<input aria-invalid={invalidFields.includes("loanNumber")} onChange={(event) => { update("loanNumber", event.currentTarget.value); }} required value={draft.loanNumber} /></label>
          <label>借贷 APP<input onChange={(event) => { update("loanAppName", event.currentTarget.value); }} value={draft.loanAppName} /></label>
          <label>借款金额<input inputMode="decimal" onChange={(event) => { update("borrowedAmount", event.currentTarget.value); }} value={draft.borrowedAmount} /></label>
          <label>借款币种<input maxLength={3} onChange={(event) => { update("borrowedCurrency", event.currentTarget.value); }} value={draft.borrowedCurrency} /></label>
          <label>负责催收员<input aria-invalid={invalidFields.includes("assignedCollectorId")} onChange={(event) => { update("assignedCollectorId", event.currentTarget.value); }} required value={draft.assignedCollectorId} /></label>
          <label>币种<input aria-invalid={invalidFields.includes("currency")} maxLength={3} minLength={3} onChange={(event) => { update("currency", event.currentTarget.value); }} required value={draft.currency} /></label>
          <label>当前逾期金额<input aria-invalid={invalidFields.includes("currentOverdueAmount")} inputMode="decimal" onChange={(event) => { update("currentOverdueAmount", event.currentTarget.value); }} pattern="\d+(?:\.\d{1,2})?" required value={draft.currentOverdueAmount} /></label>
          <label>总应还金额<input aria-invalid={invalidFields.includes("totalDueAmount")} inputMode="decimal" onChange={(event) => { update("totalDueAmount", event.currentTarget.value); }} pattern="\d+(?:\.\d{1,2})?" required value={draft.totalDueAmount} /></label>
          <label>逾期天数<input aria-invalid={invalidFields.includes("daysPastDue")} min="0" onChange={(event) => { update("daysPastDue", event.currentTarget.value); }} required type="number" value={draft.daysPastDue} /></label>
          <label>到期日<input aria-invalid={invalidFields.includes("dueDate")} onChange={(event) => { update("dueDate", event.currentTarget.value); }} required type="date" value={draft.dueDate} /></label>
          <label>客户语言<input aria-invalid={invalidFields.includes("customerLanguage")} onChange={(event) => { update("customerLanguage", event.currentTarget.value); }} required value={draft.customerLanguage} /></label>
          <label>付款链接<input aria-invalid={invalidFields.includes("paymentLink")} onChange={(event) => { update("paymentLink", event.currentTarget.value); }} pattern="https://.*" required type="url" value={draft.paymentLink} /></label>
          <label>还款方式<input onChange={(event) => { update("repaymentMethod", event.currentTarget.value); }} value={draft.repaymentMethod} /></label>
          <button type="submit">保存订单</button>
        </form>
      ) : null}
      {error === null ? null : <p role="alert">{error}</p>}
      {props.orders.length === 0 ? <p className="inspector-empty-note">暂无已保存订单。AI 候选须经人工核验后才能录入。</p> : null}
      {props.orders.map((order) => <OrderCard
        canEdit={props.canEdit}
        key={order.id}
        onCandidatePrefillConsumed={clearCandidateOrderPrefill}
        onUpdate={props.onUpdate}
        order={order}
        {...(candidateOrderPrefill?.orderId === order.id ? { candidatePrefill: candidateOrderPrefill.candidate } : {})}
      />)}
      </>}
    </section>
  );
}

function LoanCandidateReview(props: {
  canEdit: boolean;
  candidates: readonly LoanFactCandidate[];
  orders: readonly LoanOrder[];
  onPrefill: (candidate: LoanFactCandidate, orderId: string | null) => void;
  onReview?: (id: string, action: "CONFIRM" | "REJECT", reason?: string) => Promise<void>;
}): React.JSX.Element | null {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [targetByCandidate, setTargetByCandidate] = useState<Record<string, string>>({});
  if (props.candidates.length === 0) return null;
  return <section aria-label="贷款信息候选审核" className="inspector-candidates">
    <h4>从催收消息识别的贷款信息（AI 候选，需人工核验）</h4>
    {props.candidates.map((candidate) => {
      const matchingOrders = candidateMatchingOrders(candidate, props.orders);
      const selectedOrderId = targetByCandidate[candidate.id] ?? (matchingOrders.length === 1 ? matchingOrders[0]!.id : "");
      return <article className="inspector-candidate-card" key={candidate.id}>
      <div className="inspector-card-heading"><strong>待核验信息</strong><span className="inspector-status">{candidateStatusLabel(candidate.status)}</span></div>
      <p className="inspector-card-meta">模型 {candidate.modelVersion} · 规则 {candidate.rulesVersion}</p>
      <dl>{Object.entries(candidate.facts).filter(([, item]) => item.value !== null || item.evidenceText !== null).map(([field, item]) => <div key={field}>
        <dt>{candidateFieldLabel(field)}</dt>
        <dd><strong>{item.value ?? "未识别"}</strong><small>置信度 {Math.round(item.confidence * 100)}%</small></dd>
        {item.evidenceText === null ? null : <dd>原文证据：“{item.evidenceText}”</dd>}
        {field === "repaymentLink" && item.value !== null ? <dd>链接尚未验证，请勿直接视为有效还款链接。</dd> : null}
      </div>)}</dl>
      {Object.values(candidate.facts).every((item) => item.value === null && item.evidenceText === null) ? <p className="inspector-empty-note">未识别出可核验的贷款字段。</p> : null}
      {candidate.status === "PENDING_REVIEW" ? <>
        {props.orders.length === 0 ? null : <label>带入现有订单
          <select disabled={matchingOrders.length !== 1} onChange={(event) => {
            const orderId = event.currentTarget.value;
            setTargetByCandidate((current) => ({ ...current, [candidate.id]: orderId }));
          }} value={selectedOrderId}>
            <option value="">选择目标订单</option>
            {matchingOrders.map((order) => <option key={order.id} value={order.id}>{order.loanNumber} · {order.loanAppName ?? "未填写 APP"}</option>)}
          </select>
        </label>}
        {props.orders.length > 0 && matchingOrders.length !== 1 ? <p role="alert">候选生成后订单版本已变化或无法唯一匹配，请刷新核验；为避免覆盖，暂不能带入现有订单。</p> : null}
        <label>拒绝原因<input onChange={(event) => setReason(event.currentTarget.value)} value={reason} /></label>
        <button className="inspector-primary-action" disabled={!props.canEdit || props.onReview === undefined || (props.orders.length > 0 && (matchingOrders.length !== 1 || selectedOrderId !== matchingOrders[0]?.id))} onClick={() => {
          setError(null);
          void props.onReview?.(candidate.id, "CONFIRM").then(() => props.onPrefill(candidate, props.orders.length === 0 ? null : selectedOrderId)).catch(() => setError("审核失败，候选可能已更新，请刷新后重试。"));
        }} type="button">确认候选并带入订单表单（仍需人工核验并保存）</button>
        <button disabled={!props.canEdit || props.onReview === undefined || reason.trim().length === 0} onClick={() => {
          setError(null);
          void props.onReview?.(candidate.id, "REJECT", reason.trim()).then(() => setReason("")).catch(() => setError("拒绝失败，候选可能已更新，请刷新后重试。"));
        }} type="button">拒绝候选</button>
      </> : null}
      </article>;
    })}
    {error === null ? null : <p role="alert">{error}</p>}
  </section>;
}

function candidateStatusLabel(status: LoanFactCandidate["status"]): string {
  return ({ CONFIRMED: "已人工确认", CONFLICT: "存在冲突", FAILED: "提取失败", PENDING_REVIEW: "待审核", REJECTED: "已拒绝" })[status];
}

function candidateFieldLabel(field: string): string {
  return ({ appName: "借贷 APP", borrowedAmount: "借款金额", borrowedCurrency: "借款币种", dueDate: "到期日", dueTimeZone: "时区", repaymentAmount: "应还金额", repaymentCurrency: "应还币种", repaymentLink: "还款链接", repaymentMethod: "还款方式" })[field] ?? field;
}

function candidateMatchingOrders(candidate: LoanFactCandidate, orders: readonly LoanOrder[]): LoanOrder[] {
  if (candidate.sourceOrderVersion === null) return [];
  return orders.filter((order) => order.updatedAt === candidate.sourceOrderVersion);
}

function AssignmentControl(props: {
  collectors: readonly PublicUser[];
  customer: Customer;
  getHistory: (customerId: string) => Promise<CustomerAssignment[]>;
  onReassign: (customerId: string, collectorId: string) => Promise<void>;
}): React.JSX.Element {
  const [collectorId, setCollectorId] = useState(props.customer.assignedCollectorId);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<CustomerAssignment[] | null>(null);

  useEffect(() => {
    void props.getHistory(props.customer.id).then(setHistory, () => { setHistory(null); });
  }, [props.customer.id, props.getHistory]);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (collectorId === props.customer.assignedCollectorId) return;
    setError(null);
    try {
      await props.onReassign(props.customer.id, collectorId);
      setHistory(await props.getHistory(props.customer.id));
    } catch {
      setError("重新分配催收员失败，请重试。");
    }
  }

  return <form aria-label="重新分配催收员" onSubmit={(event) => { void submit(event); }}>
    <label>负责催收员
      <select onChange={(event) => { setCollectorId(event.currentTarget.value); }} value={collectorId}>
        {props.collectors.map((collector) => <option key={collector.id} value={collector.id}>{collector.email}</option>)}
      </select>
    </label>
    <button disabled={collectorId === props.customer.assignedCollectorId} type="submit">重新分配</button>
    {error === null ? null : <p role="alert">{error}</p>}
    <details>
      <summary>分配历史</summary>
      {history === null ? <p>分配历史暂不可用。</p> : history.length === 0 ? <p>暂无分配历史。</p> : <ol>
        {history.map((assignment) => <li key={assignment.id}>
          {collectorLabel(props.collectors, assignment.collectorId)} · {assignment.startedAt} 至 {assignment.endedAt ?? "当前"}
        </li>)}
      </ol>}
    </details>
  </form>;
}

function collectorLabel(collectors: readonly PublicUser[], id: string): string {
  return collectors.find((collector) => collector.id === id)?.email ?? id;
}

function CustomerProfile(props: {
  canEdit: boolean;
  canManageRestrictedFields: boolean;
  customer: Customer;
  onUpdate: (id: string, patch: CustomerPatch) => Promise<void>;
}): React.JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    automationStatus: props.customer.automationStatus,
    contactPolicy: props.customer.contactPolicy,
    contactPreference: props.customer.contactPreference ?? "",
    language: props.customer.language,
    name: props.customer.name,
    riskLevel: props.customer.riskLevel,
    tags: props.customer.tags.join(", "),
    timeZone: props.customer.timeZone
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function preventImplicitSave(event: KeyboardEvent<HTMLFormElement>): void {
    if (event.key === "Enter" && event.target instanceof HTMLInputElement) event.preventDefault();
  }

  function updateDraft<K extends keyof typeof draft>(field: K, value: (typeof draft)[K]): void {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const patch: CustomerPatch = {
        name: draft.name,
        language: draft.language,
        timeZone: draft.timeZone,
        contactPreference: draft.contactPreference.trim() || null,
        tags: [...new Set(draft.tags.split(",").map((tag) => tag.trim()).filter(Boolean))]
      };
      if (props.canManageRestrictedFields) {
        patch.riskLevel = draft.riskLevel;
        patch.contactPolicy = draft.contactPolicy;
        patch.automationStatus = draft.automationStatus;
      }
      await props.onUpdate(props.customer.id, patch);
      setEditing(false);
    } catch {
      setError("保存客户资料失败，请检查输入后重试。");
    } finally {
      setSaving(false);
    }
  }

  return editing ? <form aria-label="编辑客户资料" onKeyDown={preventImplicitSave} onSubmit={(event) => { void save(event); }}>
    <label>客户姓名<input onChange={(event) => { updateDraft("name", event.currentTarget.value); }} required value={draft.name} /></label>
    <label>电话<input disabled value={props.customer.phoneNumber} /></label>
    <label>语言<input onChange={(event) => { updateDraft("language", event.currentTarget.value); }} required value={draft.language} /></label>
    <label>时区<input onChange={(event) => { updateDraft("timeZone", event.currentTarget.value); }} required value={draft.timeZone} /></label>
    <label>联系偏好<input onChange={(event) => { updateDraft("contactPreference", event.currentTarget.value); }} value={draft.contactPreference} /></label>
    <label>标签（逗号分隔）<input onChange={(event) => { updateDraft("tags", event.currentTarget.value); }} value={draft.tags} /></label>
    {props.canManageRestrictedFields ? <>
      <label>风险<select onChange={(event) => { updateDraft("riskLevel", event.currentTarget.value as Customer["riskLevel"]); }} value={draft.riskLevel}><option>LOW</option><option>MEDIUM</option><option>HIGH</option><option>UNKNOWN</option></select></label>
      <label>联系策略<select onChange={(event) => { updateDraft("contactPolicy", event.currentTarget.value as Customer["contactPolicy"]); }} value={draft.contactPolicy}><option>CONTACT_ALLOWED</option><option>DO_NOT_CONTACT</option></select></label>
      <label>自动化<select onChange={(event) => { updateDraft("automationStatus", event.currentTarget.value as Customer["automationStatus"]); }} value={draft.automationStatus}><option>ANALYSIS_ONLY</option><option>DISABLED</option></select></label>
    </> : null}
    <button disabled={saving} type="submit">{saving ? "保存中…" : "保存客户资料"}</button><button disabled={saving} onClick={() => { setEditing(false); }} type="button">取消</button>
    {error === null ? null : <p role="alert">{error}</p>}
  </form> : <>
    {props.customer.phoneNumber.startsWith("wa-lid:") ? <p className="wa-unresolved-note">此联系人号码尚未由 WhatsApp 映射；资料暂不完整，请勿按手机号识别客户。</p> : null}
    <dl>
      <dt>客户</dt><dd>{props.customer.name}</dd>
      <dt>电话</dt><dd>{customerPhoneLabel(props.customer.phoneNumber)}</dd>
      <dt>语言</dt><dd>{props.customer.language}</dd>
      <dt>风险</dt><dd>{props.customer.riskLevel}</dd>
      <dt>联系策略</dt><dd>{props.customer.contactPolicy}</dd>
      <dt>标签</dt><dd>{props.customer.tags.join("、") || "无"}</dd>
    </dl>
    {props.canEdit ? <button onClick={() => { setEditing(true); }} type="button">编辑客户资料</button> : null}
  </>;
}

function customerPhoneLabel(phoneNumber: string): string {
  return phoneNumber.startsWith("wa-lid:")
    ? "待 WhatsApp 提供号码映射"
    : phoneNumber;
}

function OrderCard(props: {
  canEdit: boolean;
  candidatePrefill?: LoanFactCandidate;
  onCandidatePrefillConsumed: () => void;
  onUpdate: (id: string, facts: LoanOrderFacts) => Promise<void>;
  order: LoanOrder;
}): React.JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(orderFacts(props.order));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (props.candidatePrefill === undefined) return;
    const facts = props.candidatePrefill.facts;
    setDraft((current) => ({
      ...current,
      borrowedAmount: facts.borrowedAmount.value ?? current.borrowedAmount,
      borrowedCurrency: facts.borrowedCurrency.value ?? current.borrowedCurrency,
      currency: facts.repaymentCurrency.value ?? facts.borrowedCurrency.value ?? current.currency,
      dueDate: facts.dueDate.value ?? current.dueDate,
      loanAppName: facts.appName.value ?? current.loanAppName,
      paymentLink: facts.repaymentLink.value ?? current.paymentLink,
      repaymentMethod: facts.repaymentMethod.value ?? current.repaymentMethod,
      totalDueAmount: facts.repaymentAmount.value ?? current.totalDueAmount
    }));
    setEditing(true);
    props.onCandidatePrefillConsumed();
  }, [props.candidatePrefill, props.onCandidatePrefillConsumed]);

  async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    try {
      await props.onUpdate(props.order.id, {
        ...draft,
        currency: draft.currency.toUpperCase(),
        daysPastDue: Number(draft.daysPastDue)
      });
      setEditing(false);
    } catch {
      setError("更新订单失败，请检查必填字段后重试。");
    }
  }

  function update(field: keyof typeof draft, value: string): void {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  return (
    <article className="inspector-order-card">
      <h4>{props.order.loanNumber}</h4>
      {editing ? <form aria-label={`编辑订单 ${props.order.loanNumber}`} onSubmit={(event) => { void save(event); }}>
        <label>贷款编号<input onChange={(event) => { update("loanNumber", event.currentTarget.value); }} required value={draft.loanNumber} /></label>
        <label>借贷 APP<input onChange={(event) => { update("loanAppName", event.currentTarget.value ?? ""); }} value={draft.loanAppName ?? ""} /></label>
        <label>借款金额<input inputMode="decimal" onChange={(event) => { update("borrowedAmount", event.currentTarget.value ?? ""); }} value={draft.borrowedAmount ?? ""} /></label>
        <label>借款币种<input maxLength={3} onChange={(event) => { update("borrowedCurrency", event.currentTarget.value ?? ""); }} value={draft.borrowedCurrency ?? ""} /></label>
        <label>负责催收员<input onChange={(event) => { update("assignedCollectorId", event.currentTarget.value); }} required value={draft.assignedCollectorId} /></label>
        <label>币种<input maxLength={3} minLength={3} onChange={(event) => { update("currency", event.currentTarget.value); }} required value={draft.currency} /></label>
        <label>当前逾期金额<input inputMode="decimal" onChange={(event) => { update("currentOverdueAmount", event.currentTarget.value); }} required value={draft.currentOverdueAmount} /></label>
        <label>总应还金额<input inputMode="decimal" onChange={(event) => { update("totalDueAmount", event.currentTarget.value); }} required value={draft.totalDueAmount} /></label>
        <label>逾期天数<input min="0" onChange={(event) => { update("daysPastDue", event.currentTarget.value); }} required type="number" value={draft.daysPastDue} /></label>
        <label>到期日<input onChange={(event) => { update("dueDate", event.currentTarget.value); }} required type="date" value={draft.dueDate} /></label>
        <label>客户语言<input onChange={(event) => { update("customerLanguage", event.currentTarget.value); }} required value={draft.customerLanguage} /></label>
        <label>付款链接<input onChange={(event) => { update("paymentLink", event.currentTarget.value); }} required type="url" value={draft.paymentLink} /></label>
        <label>还款方式<input onChange={(event) => { update("repaymentMethod", event.currentTarget.value ?? ""); }} value={draft.repaymentMethod ?? ""} /></label>
        <button type="submit">保存修改</button>
        <button onClick={() => { setEditing(false); setDraft(orderFacts(props.order)); }} type="button">取消</button>
      </form> : <>
        <dl>
          <dt>当前逾期</dt><dd>{props.order.currency} {props.order.currentOverdueAmount}</dd>
          <dt>总应还</dt><dd>{props.order.currency} {props.order.totalDueAmount}</dd>
          <dt>逾期天数</dt><dd>{props.order.daysPastDue}</dd>
          <dt>到期日</dt><dd>{props.order.dueDate}</dd>
        </dl>
        <a href={props.order.paymentLink} rel="noreferrer" target="_blank">查看付款链接</a>
        {props.canEdit ? <button onClick={() => { setEditing(true); }} type="button">编辑订单</button> : null}
      </>}
      {error === null ? null : <p role="alert">{error}</p>}
    </article>
  );
}

function orderFacts(order: LoanOrder): Omit<LoanOrderFacts, "daysPastDue"> & { daysPastDue: string } {
  return {
    assignedCollectorId: order.assignedCollectorId,
    borrowedAmount: order.borrowedAmount ?? null,
    borrowedCurrency: order.borrowedCurrency ?? null,
    currency: order.currency,
    currentOverdueAmount: order.currentOverdueAmount,
    customerLanguage: order.customerLanguage,
    daysPastDue: String(order.daysPastDue),
    dueDate: order.dueDate,
    loanNumber: order.loanNumber,
    loanAppName: order.loanAppName ?? null,
    paymentLink: order.paymentLink,
    repaymentMethod: order.repaymentMethod ?? null,
    totalDueAmount: order.totalDueAmount
  };
}

function invalidOrderFields(error: unknown): string[] {
  if (!(error instanceof ApiError) || !isRecord(error.details)) return [];
  const fields = error.details["fields"];
  return Array.isArray(fields)
    ? fields.filter((field): field is string => typeof field === "string")
    : [];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
